/**
 * The DOM layer. Tagged-template-free on purpose: everything here builds real nodes with
 * `createElement`, `createTextNode` and `setAttribute`.
 *
 * There is no `innerHTML` anywhere in this file, and there must not be one anywhere in the
 * app. That is what lets PadLab ship a Content-Security-Policy with no `'unsafe-inline'`
 * for scripts and no `'unsafe-eval'` at all. Styles are written through the CSSOM
 * (`style.setProperty`), which CSP does not police, rather than through a `style`
 * attribute, which it does.
 *
 * Visual updates are coalesced into one animation frame by `schedule`. Audio never waits
 * on any of this: a pointer handler calls the engine directly and only then sets a signal.
 */

import { effect, isSignal, isWritableSignal, type ReadonlySignal, type Signal } from './signal';
import { createLogger, tag } from './log';

const log = createLogger('dom');

// MARK: - Frame scheduling

const frameQueue = new Set<() => void>();
let frameHandle = 0;

const requestFrame: (callback: () => void) => number =
  typeof requestAnimationFrame === 'function'
    ? (callback) => requestAnimationFrame(() => callback())
    : (callback) => setTimeout(callback, 16) as unknown as number;

function flushFrame(): void {
  frameHandle = 0;
  // Snapshot first: a task that schedules another task must land in the *next* frame,
  // otherwise a self-scheduling animation would spin inside this one.
  const tasks = [...frameQueue];
  frameQueue.clear();
  for (const task of tasks) task();
}

/**
 * Queues `fn` to run on the next animation frame. Queuing the same function repeatedly
 * before the frame fires runs it once — which is how a signal that changes forty times in
 * one gesture still produces exactly one DOM write.
 *
 * Returns a function that cancels the pending run.
 */
export function schedule(fn: () => void): () => void {
  frameQueue.add(fn);
  if (frameHandle === 0) frameHandle = requestFrame(flushFrame);
  return () => {
    frameQueue.delete(fn);
  };
}

// MARK: - Node lifetime

const cleanups = new WeakMap<Node, Array<() => void>>();

/** Registers work to undo when `node` leaves the tree via `disposeTree` or `mount`'s disposer. */
export function onCleanup(node: Node, fn: () => void): void {
  const existing = cleanups.get(node);
  if (existing) existing.push(fn);
  else cleanups.set(node, [fn]);
}

function runCleanups(node: Node): void {
  const list = cleanups.get(node);
  if (list === undefined) return;
  cleanups.delete(node);
  for (const fn of list) fn();
}

/** Runs every registered cleanup on `root` and its descendants. Does not remove anything. */
export function disposeTree(root: Node): void {
  runCleanups(root);
  if (root.nodeType !== 1 && root.nodeType !== 11) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) runCleanups(node);
}

/** Appends `node` to `parent`. The returned disposer removes it and releases its bindings. */
export function mount(parent: Node, node: Node): () => void {
  parent.appendChild(node);
  return () => {
    disposeTree(node);
    node.parentNode?.removeChild(node);
  };
}

/** Empties an element, releasing every binding inside it. The CSP-safe `innerHTML = ''`. */
export function clear(node: Node): void {
  while (node.firstChild !== null) {
    const child = node.firstChild;
    disposeTree(child);
    node.removeChild(child);
  }
}

/** Adds a listener and returns its remover, so global handlers cannot outlive a view. */
export function on<K extends keyof WindowEventMap>(
  target: Window,
  type: K,
  handler: (event: WindowEventMap[K]) => void,
  options?: AddEventListenerOptions,
): () => void;
export function on<K extends keyof DocumentEventMap>(
  target: Document,
  type: K,
  handler: (event: DocumentEventMap[K]) => void,
  options?: AddEventListenerOptions,
): () => void;
export function on<K extends keyof HTMLElementEventMap>(
  target: HTMLElement,
  type: K,
  handler: (event: HTMLElementEventMap[K]) => void,
  options?: AddEventListenerOptions,
): () => void;
export function on(
  target: EventTarget,
  type: string,
  handler: (event: Event) => void,
  options?: AddEventListenerOptions,
): () => void;
export function on(
  target: EventTarget,
  type: string,
  handler: EventListenerOrEventListenerObject,
  options?: AddEventListenerOptions,
): () => void {
  target.addEventListener(type, handler, options);
  return () => target.removeEventListener(type, handler, options);
}

// MARK: - Value plumbing

export type AttrValue = string | number | boolean | null | undefined;

export type ClassValue =
  | string
  | number
  | false
  | null
  | undefined
  | ClassValue[]
  | Record<string, boolean | null | undefined>;

export type StyleValue = Record<string, string | number | null | undefined>;

export type Child =
  | Node
  | string
  | number
  | boolean
  | null
  | undefined
  | ReadonlySignal<unknown>
  | Child[];

function stringify(value: unknown): string {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : String(value);
}

function classString(value: ClassValue): string {
  if (value === null || value === undefined || value === false) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    const parts: string[] = [];
    for (const entry of value) {
      const part = classString(entry);
      if (part !== '') parts.push(part);
    }
    return parts.join(' ');
  }
  const parts: string[] = [];
  for (const key of Object.keys(value)) if (value[key] === true) parts.push(key);
  return parts.join(' ');
}

/** camelCase → kebab-case, leaving custom properties (`--pad-gap`) untouched. */
function cssName(name: string): string {
  return name.startsWith('--') ? name : name.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}

/**
 * Applies `source` immediately, then on every change through one shared animation frame.
 * The first application is synchronous so a freshly built view is never blank for a frame.
 */
function bindValue<T>(source: ReadonlySignal<T>, apply: (value: T) => void): () => void {
  let latest!: T;
  let primed = false;
  let cancel: (() => void) | null = null;
  const write = (): void => {
    cancel = null;
    apply(latest);
  };
  const dispose = effect(() => {
    latest = source.value;
    if (!primed) {
      primed = true;
      apply(latest);
      return;
    }
    if (cancel === null) cancel = schedule(write);
  });
  return () => {
    dispose();
    cancel?.();
    cancel = null;
  };
}

/** Mirrors a signal into a node's text. Works on both elements and text nodes. */
export function bindText(node: Node, source: ReadonlySignal<unknown>): () => void {
  return bindValue(source, (value) => {
    const text = stringify(value);
    if (node.nodeType === 3) (node as Text).data = text;
    else node.textContent = text;
  });
}

/** Toggles one class name from a boolean signal. */
export function bindClass(
  element: Element,
  name: string,
  source: ReadonlySignal<boolean>,
): () => void {
  return bindValue(source, (present) => {
    element.classList.toggle(name, present === true);
  });
}

/** Sets or removes one attribute. `false`, `null` and `undefined` remove it. */
export function bindAttr(
  element: Element,
  name: string,
  source: ReadonlySignal<AttrValue>,
): () => void {
  return bindValue(source, (value) => {
    setAttribute(element, name, value);
  });
}

/** Mirrors a signal into a CSS custom property or declaration on one element. */
export function bindStyle(
  element: Element,
  property: string,
  source: ReadonlySignal<string | number | null | undefined>,
): () => void {
  const style = (element as HTMLElement).style;
  const name = cssName(property);
  return bindValue(source, (value) => {
    if (value === null || value === undefined || value === '') style.removeProperty(name);
    else style.setProperty(name, typeof value === 'number' ? String(value) : value);
  });
}

// MARK: - Attributes and props

/** Attributes whose live state lives on the property, not the markup. */
function setAttribute(element: Element, name: string, value: AttrValue): void {
  if (name === 'value' && 'value' in element) {
    const next = stringify(value);
    if ((element as HTMLInputElement).value !== next) (element as HTMLInputElement).value = next;
    return;
  }
  if (name === 'checked' && 'checked' in element) {
    (element as HTMLInputElement).checked = value === true;
    return;
  }
  if (name === 'indeterminate' && 'indeterminate' in element) {
    (element as HTMLInputElement).indeterminate = value === true;
    return;
  }
  if (value === null || value === undefined || value === false) {
    element.removeAttribute(name);
    return;
  }
  element.setAttribute(name, value === true ? '' : stringify(value));
}

export interface ElementProps {
  class?: ClassValue | ReadonlySignal<ClassValue>;
  style?: StyleValue | ReadonlySignal<StyleValue>;
  dataset?: Record<string, AttrValue | ReadonlySignal<AttrValue>>;
  /**
   * Two-way on `<input>`, `<textarea>` and `<select>` (value, or `checked` for checkboxes
   * and radios); one-way into `textContent` on anything else. A read-only signal binds one
   * way in both cases.
   */
  bind?: ReadonlySignal<string> | ReadonlySignal<number> | ReadonlySignal<boolean>;
  ref?: (element: never) => void;
  [name: string]: unknown;
}

function applyClass(element: Element, value: unknown): void {
  if (isSignal(value)) {
    onCleanup(
      element,
      bindValue(value as ReadonlySignal<ClassValue>, (next) => {
        setAttribute(element, 'class', classString(next));
      }),
    );
    return;
  }
  const text = classString(value as ClassValue);
  if (text !== '') element.setAttribute('class', text);
}

function applyStyle(element: Element, value: unknown): void {
  const style = (element as HTMLElement).style;
  const write = (declarations: StyleValue): void => {
    for (const key of Object.keys(declarations)) {
      const entry = declarations[key];
      const name = cssName(key);
      if (entry === null || entry === undefined || entry === '') style.removeProperty(name);
      else style.setProperty(name, typeof entry === 'number' ? String(entry) : entry);
    }
  };
  if (isSignal(value)) {
    onCleanup(element, bindValue(value as ReadonlySignal<StyleValue>, write));
    return;
  }
  write(value as StyleValue);
}

function applyDataset(element: Element, value: Record<string, unknown>): void {
  for (const key of Object.keys(value)) {
    const entry = value[key];
    const name = `data-${cssName(key)}`;
    if (isSignal(entry)) {
      onCleanup(element, bindAttr(element, name, entry as ReadonlySignal<AttrValue>));
    } else {
      setAttribute(element, name, entry as AttrValue);
    }
  }
}

function applyListener(element: Element, key: string, value: unknown): void {
  const type = key.slice(2).toLowerCase();
  const [handler, options] = Array.isArray(value)
    ? (value as [EventListener, AddEventListenerOptions | undefined])
    : [value as EventListener, undefined];
  if (typeof handler !== 'function') return;
  element.addEventListener(type, handler, options);
  onCleanup(element, () => element.removeEventListener(type, handler, options));
}

function applyBind(element: Element, source: unknown): void {
  if (!isSignal(source)) return;
  const isField =
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement;

  if (!isField) {
    onCleanup(element, bindText(element, source));
    return;
  }

  const checkable =
    element instanceof HTMLInputElement &&
    (element.type === 'checkbox' || element.type === 'radio');
  const numeric = typeof source.peek() === 'number';

  onCleanup(
    element,
    bindValue(source, (value) => {
      if (checkable) {
        (element as HTMLInputElement).checked = value === true;
        return;
      }
      const next = stringify(value);
      const field = element as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (field.value !== next) field.value = next;
    }),
  );

  if (!isWritableSignal(source)) return;
  const writable = source as Signal<unknown>;
  const type = checkable || element instanceof HTMLSelectElement ? 'change' : 'input';
  const handler = (): void => {
    if (checkable) {
      writable.value = (element as HTMLInputElement).checked;
      return;
    }
    const raw = (element as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value;
    writable.value = numeric ? Number.parseFloat(raw) : raw;
  };
  element.addEventListener(type, handler);
  onCleanup(element, () => element.removeEventListener(type, handler));
}

function applyProps(element: Element, props: ElementProps): void {
  for (const key of Object.keys(props)) {
    const value = props[key];
    if (value === undefined) continue;
    switch (key) {
      case 'class':
        applyClass(element, value);
        continue;
      case 'style':
        applyStyle(element, value);
        continue;
      case 'dataset':
        applyDataset(element, value as Record<string, unknown>);
        continue;
      case 'bind':
        applyBind(element, value);
        continue;
      case 'ref':
        (value as (node: Element) => void)(element);
        continue;
      default:
        break;
    }
    if (key.length > 2 && key.startsWith('on')) {
      applyListener(element, key, value);
      continue;
    }
    if (isSignal(value)) {
      onCleanup(element, bindAttr(element, key, value as ReadonlySignal<AttrValue>));
      continue;
    }
    setAttribute(element, key, value as AttrValue);
  }
}

// MARK: - Construction

function appendChild(parent: Node, child: Child): void {
  if (child === null || child === undefined || child === false || child === true) return;
  if (Array.isArray(child)) {
    for (const entry of child) appendChild(parent, entry);
    return;
  }
  if (isSignal(child)) {
    const node = document.createTextNode('');
    onCleanup(node, bindText(node, child));
    parent.appendChild(node);
    return;
  }
  if (typeof child === 'object' && 'nodeType' in child) {
    parent.appendChild(child);
    return;
  }
  parent.appendChild(document.createTextNode(stringify(child)));
}

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: ElementProps | null,
  ...children: Child[]
): HTMLElementTagNameMap[K];
export function h(tag: string, props?: ElementProps | null, ...children: Child[]): HTMLElement;
export function h(tag: string, props?: ElementProps | null, ...children: Child[]): HTMLElement {
  const element = document.createElement(tag);
  if (props) applyProps(element, props);
  for (const child of children) appendChild(element, child);
  return element;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/** SVG needs its own namespace; icons are built here rather than pasted as markup. */
export function svg<K extends keyof SVGElementTagNameMap>(
  tag: K,
  props?: ElementProps | null,
  ...children: Child[]
): SVGElementTagNameMap[K];
export function svg(tag: string, props?: ElementProps | null, ...children: Child[]): SVGElement;
export function svg(tag: string, props?: ElementProps | null, ...children: Child[]): SVGElement {
  const element = document.createElementNS(SVG_NS, tag);
  if (props) applyProps(element, props);
  for (const child of children) appendChild(element, child);
  return element;
}

/** A DocumentFragment of children — for returning several siblings from one function. */
export function frag(...children: Child[]): DocumentFragment {
  const fragment = document.createDocumentFragment();
  for (const child of children) appendChild(fragment, child);
  return fragment;
}

// MARK: - Keyed lists

/**
 * Keyed reconciliation over a container the list owns exclusively.
 *
 * Rows that keep their key keep their element — the pad grid must not rebuild a tile
 * because a neighbouring pad changed, or a pressed tile would lose its pointer capture
 * mid-gesture. Only genuinely new keys call `render`; only removed keys are torn down.
 *
 * `update` is called for every retained row on every pass, including when the item is
 * identical, because PadLab's model objects are mutated in place by `sanitiseProject` and
 * identity comparison would miss those edits. Keep `update` to property writes.
 */
export function list<T>(
  container: Element,
  items: ReadonlySignal<readonly T[]>,
  keyOf: (item: T, index: number) => string,
  render: (item: T, index: number) => Element,
  update?: (element: Element, item: T, index: number) => void,
): () => void {
  let entries = new Map<string, Element>();

  const apply = (source: readonly T[]): void => {
    const next = new Map<string, Element>();
    const order: string[] = [];

    for (let index = 0; index < source.length; index++) {
      const item = source[index]!;
      let key = keyOf(item, index);
      if (next.has(key)) {
        // A duplicate key would make two rows fight over one element. Disambiguate rather
        // than throw — the data is the user's, and a broken list is worse than a warning.
        log.warn('Duplicate list key, disambiguating by position', { key: tag(key), index });
        key = `${key} ${index}`;
      }
      const existing = entries.get(key);
      if (existing !== undefined) {
        update?.(existing, item, index);
        next.set(key, existing);
      } else {
        next.set(key, render(item, index));
      }
      order.push(key);
    }

    for (const [key, element] of entries) {
      if (next.has(key)) continue;
      disposeTree(element);
      element.remove();
    }

    // Walk the desired order against the live children, moving only what is out of place.
    let cursor: ChildNode | null = container.firstChild;
    for (const key of order) {
      const element = next.get(key)!;
      if (cursor === element) {
        cursor = cursor.nextSibling;
        continue;
      }
      container.insertBefore(element, cursor);
    }

    entries = next;
  };

  const dispose = bindValue(items, apply);
  const teardown = (): void => {
    dispose();
    for (const element of entries.values()) {
      disposeTree(element);
      element.remove();
    }
    entries.clear();
  };
  onCleanup(container, teardown);
  return teardown;
}
